import React, { useContext, useState } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import CustomerTable from "./CustomerListTable";
import NoCustomerListFound from "./NoCustomerListFound";
import AddCustomerModal from "./AddCustomerModal";
import AppButton from "../common/AppButton";
import EditCustomerModal from "./EditCustomerModal";
import ViewCustomerModal from "./ViewCustomerModal";
import { Customer } from "./types/customer";
import { useGetCustomerList } from "../../hooks/api/useCustomer";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import {
  KivuI18nContext,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";

interface CustomerListProps {
  companyId?: string;
  /** Store from route; list and create are scoped to this store only. */
  contextStoreId: string;
}

const CustomerList: React.FC<CustomerListProps> = ({
  companyId,
  contextStoreId
}) => {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );

  const { t } = useKivunovaTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const customerList = useSelector(
    (state: RootState) => state.customers.customerList
  );
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const effectiveCompanyId = companyId || userDetails?.companyId || "COMP-001";

  const storeId = String(contextStoreId ?? "").trim();
  const listStoreFilter = storeId || undefined;

  const {
    isPending,
    data: queryData,
    refetch: refetchCustomerList
  } = useGetCustomerList(effectiveCompanyId, listStoreFilter);

  const displayCustomers =
    queryData && queryData.length > 0 ? queryData : customerList;

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setOpenEditModal(true);
  };

  const handleView = (customer: Customer) => {
    setSelectedCustomer(customer);
    setOpenViewModal(true);
  };

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-customers", "Loading customers...")}
      />
    );
  }

  if (!displayCustomers.length) {
    return (
      <>
        <NoCustomerListFound onAddClick={() => setOpenAddModal(true)} />
        <AddCustomerModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          fixedStoreId={storeId || undefined}
          onCustomerCreated={refetchCustomerList}
        />
      </>
    );
  }

  return (
    <Box width="100%">
      <Box display="flex" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Box flexGrow={1} display="flex" justifyContent="flex-end">
          <AppButton variant="primary" onClick={() => setOpenAddModal(true)}>
            {t("add-new-customer", "Add New Customer")}
          </AppButton>
        </Box>
      </Box>

      <CustomerTable
        customers={displayCustomers}
        title={t("customers-title", "Customers")}
        onEdit={handleEdit}
        onView={handleView}
      />

      <AddCustomerModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        fixedStoreId={storeId || undefined}
        onCustomerCreated={refetchCustomerList}
      />

      {selectedCustomer && (
        <>
          <EditCustomerModal
            open={openEditModal}
            onClose={() => {
              setOpenEditModal(false);
              setSelectedCustomer(null);
            }}
            customer={selectedCustomer}
          />

          <ViewCustomerModal
            open={openViewModal}
            onClose={() => {
              setOpenViewModal(false);
              setSelectedCustomer(null);
            }}
            customer={selectedCustomer}
            onDelete={() => {
              setOpenViewModal(false);
              setSelectedCustomer(null);
            }}
          />
        </>
      )}
    </Box>
  );
};

export default CustomerList;
